import { useEffect } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useTrackingAllowed } from '../../shared/analytics';

const FIRED_PIXELS_KEY = '__vibesitesImageTrackingPixels';

function getFiredPixels(): Set<string> {
  const win = window as Window & { [FIRED_PIXELS_KEY]?: Set<string> };
  if (!win[FIRED_PIXELS_KEY]) win[FIRED_PIXELS_KEY] = new Set();
  return win[FIRED_PIXELS_KEY]!;
}

function isValidPixelUrl(value: string): boolean {
  try {
    return new URL(value).protocol === 'https:';
  } catch {
    return false;
  }
}

function ImageTrackingPixelComponent(_props: BlockProps) {
  const pixelUrl = useBlockToken('pixel_url', { defaultValue: '' })?.trim();
  const trackingAllowed = useTrackingAllowed('advertising');

  useEffect(() => {
    if (!trackingAllowed || !pixelUrl || !isValidPixelUrl(pixelUrl)) return;

    const fireKey = `${window.location.pathname}|${pixelUrl}`;
    const firedPixels = getFiredPixels();
    if (firedPixels.has(fireKey)) return;
    firedPixels.add(fireKey);

    const img = document.createElement('img');
    img.src = pixelUrl;
    img.dataset.vibesitesBlock = 'image-tracking-pixel';
    img.style.display = 'none';
    img.width = 0;
    img.height = 0;
    img.alt = '';
    document.body.appendChild(img);
  }, [trackingAllowed, pixelUrl]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'ImageTrackingPixel',
  displayName: 'Image Tracking Pixel',
  tokens: {
    pixel_url: {
      label: 'Pixel URL',
      type: 'url',
      description: 'Full HTTPS image tracking pixel URL, including any vendor query params.',
      required: true,
    },
  },
};

export const imageTrackingPixelBlock: BlockDefinition = {
  id: 'image-tracking-pixel',
  name: 'Image Tracking Pixel',
  description: 'Hidden image beacon for simple page-load tracking pixels. Fires only on production deployments.',
  category: 'Analytics',
  placement: 'injection',
  defaultScope: 'global',
  component: ImageTrackingPixelComponent,
  contentSchema,
  tags: ['tracking', 'pixel', 'analytics', 'image', 'beacon'],
};
